/**
 * ndvi-color.ts — Semáforo de color para NDVI mean, reusado por el mapa
 * (MapPage.tsx, capa NDVI) y por el detalle de lote (LoteDetailPage.tsx,
 * ndviColor). Mismos 3 umbrales en ambos — no inventar una escala nueva acá,
 * el usuario que ya conoce un semáforo debe reconocer el otro.
 */
export function ndviMeanToColor(mean: number | null | undefined): string {
  if (mean === null || mean === undefined) return '#9ca3af'; // gris — sin dato
  if (mean >= 0.5) return '#107c10';
  if (mean >= 0.3) return '#ca5010';
  return '#d83b01';
}

/** Modo de escala del NDVI: fija (por defecto, semáforo de umbrales) o dinámica (rango observado). */
export type NdviScaleMode = 'FIXED' | 'DYNAMIC';

export interface NdviRange { min: number; max: number }

/** Rango mínimo aceptable para la escala dinámica; por debajo cae a la fija. */
const MIN_DYNAMIC_SPAN = 0.02;

/**
 * Rango observado (min..max) de las medias NDVI, ignorando los sin dato.
 * Devuelve `null` con menos de 2 valores o si el rango es menor a 0.02
 * (en ese caso la escala dinámica no aporta contraste y se usa la fija).
 */
export function computeDynamicNdviRange(means: Array<number | null | undefined>): NdviRange | null {
  const values = means.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (max - min < MIN_DYNAMIC_SPAN) return null;
  return { min, max };
}

/**
 * Color del semáforo con escala dinámica: la media se normaliza al rango
 * observado y se elige el color de los cortes 1/3 y 2/3 del rango.
 * Sin rango (`null`) equivale a `ndviMeanToColor`.
 */
export function ndviMeanToColorScaled(mean: number | null | undefined, range: NdviRange | null): string {
  if (mean === null || mean === undefined) return ndviMeanToColor(null);
  if (!range) return ndviMeanToColor(mean);
  const span = range.max - range.min;
  const t = span > 0 ? (mean - range.min) / span : 0.5;
  if (t < 1 / 3) return ndviMeanToColor(0.1);
  if (t < 2 / 3) return ndviMeanToColor(0.4);
  return ndviMeanToColor(0.6);
}
